import { useEffect, useId, useState } from 'react';
import Button from '../ui/Button';
import { CustomerPicker } from '../nursery/CustomerPicker';
import nurseryService from '../../services/nursery';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import type { Call } from '../../types/sales';
import type { NurseryCustomer, NurseryCustomerContact } from '../../types/nursery';

/**
 * Saves the number of a call nobody had on file to a customer, once whoever
 * is on the call knows who it is: as the customer's phone while it has none,
 * onto one of its contacts with a free phone or mobile, or as a new contact
 * (name optional). The server never replaces a number already stored, and
 * matches every earlier call and voicemail from the number to the customer.
 */

type Target = 'customer' | 'new_contact' | `contact:${number}`;

const contactName = (c: NurseryCustomerContact) =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || c.position || 'Unnamed contact';

let customersCache: NurseryCustomer[] | null = null;

export default function SaveNumberForm({ callId, number, onSaved, onCancel }: {
  callId: number;
  number: string;
  onSaved: (call: Call) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [customers, setCustomers] = useState<NurseryCustomer[]>(customersCache ?? []);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [detail, setDetail] = useState<NurseryCustomer | null>(null);
  const [target, setTarget] = useState<Target>('new_contact');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getCustomers({ active: true })
      .then((res) => {
        if (!res.success) return;
        customersCache = res.data.filter((c) => !c.is_walk_in);
        setCustomers(customersCache);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the customers')));
  }, []);

  useEffect(() => {
    setDetail(null);
    if (customerId == null) return;
    let live = true;
    nurseryService.getCustomer(customerId)
      .then((res) => {
        if (!live || !res.success) return;
        setDetail(res.data);
        setTarget(res.data.phone ? 'new_contact' : 'customer');
      })
      .catch((e) => { if (live) setError(errorMessage(e, 'Could not load the customer')); });
    return () => { live = false; };
  }, [customerId]);

  const contacts = detail?.contacts ?? [];

  const save = async () => {
    if (customerId == null) return;
    setSaving(true);
    setError(null);
    try {
      const res = await salesService.saveNumber(callId, target.startsWith('contact:')
        ? { customer_id: customerId, save_as: 'contact', contact_id: Number(target.slice(8)) }
        : target === 'customer'
          ? { customer_id: customerId, save_as: 'customer' }
          : { customer_id: customerId, save_as: 'new_contact', first_name: firstName.trim(), last_name: lastName.trim() });
      if (res.success) onSaved(res.data);
      else setError(errorMessage(res, 'Could not save the number'));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the number'));
    } finally {
      setSaving(false);
    }
  };

  const radio = (value: Target, label: string, disabled = false, hint?: string) => (
    <label className={`flex items-start gap-2 text-sm ${disabled ? 'text-neutral-500' : 'text-neutral-800'}`}>
      <input type="radio" name={`${id}-target`} className="mt-0.5" checked={target === value} disabled={disabled}
        onChange={() => setTarget(value)} />
      <span className="min-w-0 break-words">{label}{hint && <span className="block text-xs text-neutral-500">{hint}</span>}</span>
    </label>
  );

  return (
    <div className="space-y-3 min-w-0">
      <div>
        <label htmlFor={`${id}-customer`} className="block text-sm font-medium text-neutral-700 mb-1">
          Save {number} to
        </label>
        <CustomerPicker id={`${id}-customer`} customers={customers} value={customerId} onChange={setCustomerId} />
      </div>

      {customerId != null && !detail && !error && <p className="text-sm text-neutral-500">Loading the customer…</p>}

      {detail && (
        <fieldset className="space-y-2 min-w-0">
          <legend className="text-sm font-medium text-neutral-700 mb-1">As</legend>
          {radio('customer', "The customer's phone number", !!detail.phone,
            detail.phone ? `Already ${detail.phone}` : undefined)}
          {contacts.map((c) => {
            const full = !!c.phone && !!c.mobile;
            return radio(`contact:${c.id}`, contactName(c), full,
              full ? 'Has a phone and a mobile already' : [c.phone, c.mobile].filter(Boolean).join(' · ') || undefined);
          })}
          {radio('new_contact', 'A new contact')}
          {target === 'new_contact' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6 min-w-0">
              <input aria-label="First name" placeholder="First name" value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              <input aria-label="Last name" placeholder="Last name" value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
        </fieldset>
      )}

      {error && <p className="text-sm text-red-700 break-words">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={!detail || saving}>{saving ? 'Saving…' : 'Save number'}</Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}
